import type { SeedingGroup } from './group-seeding';
import type { CommentCardImage } from './render-comment-card';

/**
 * Обложка блока — картинкой того же вида, что у Артура в отчёте: кадр из
 * сетки профиля. Обложка рилса приходит вертикальной 9:16, в сетке Instagram
 * она показывается срезом 3:4 по центру — так её и режем. Целиком 9:16 заняла
 * бы в документе полстраницы.
 *
 * Картинка уже перезалита к нам (см. attach-covers): наше хранилище отдаёт её
 * браузеру, и холст не «пачкается» чужим источником.
 */

/** Ширина на странице в точках 96 dpi — 180 PT, как кадр на скрине образца. */
export const COVER_WIDTH = 240;
/** Рисуем вдвое плотнее: при печати и приближении кадр не мылится. */
const SCALE = 2;
/** Сетка профиля Instagram — 3:4. */
const GRID_RATIO = 3 / 4;

export type CoverImage = CommentCardImage;

/** Какую часть картинки брать: выше 3:4 — срез по центру, иначе целиком. */
export function coverCrop(width: number, height: number): { sx: number; sy: number; sw: number; sh: number } {
  if (width / height >= GRID_RATIO) return { sx: 0, sy: 0, sw: width, sh: height };
  const sh = Math.round(width / GRID_RATIO);
  return { sx: 0, sy: Math.round((height - sh) / 2), sw: width, sh };
}

export async function renderCover(url: string): Promise<CoverImage> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`обложка не скачалась: ${res.status}`);
  const bitmap = await createImageBitmap(await res.blob());
  try {
    const { sx, sy, sw, sh } = coverCrop(bitmap.width, bitmap.height);
    const height = Math.round((COVER_WIDTH * sh) / sw);
    const canvas = document.createElement('canvas');
    canvas.width = COVER_WIDTH * SCALE;
    canvas.height = height * SCALE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas недоступен');
    ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL('image/jpeg', 0.9), width: COVER_WIDTH, height };
  } finally {
    bitmap.close();
  }
}

/**
 * Обложки всех блоков по адресу обложки. Сбой на одной не роняет выгрузку:
 * этот блок просто уйдёт без картинки.
 */
export async function renderCovers(groups: SeedingGroup[]): Promise<Map<string, CoverImage>> {
  const covers = new Map<string, CoverImage>();
  const urls = [...new Set(groups.map((g) => g.cover).filter((u): u is string => Boolean(u)))];
  await Promise.all(
    urls.map(async (url) => {
      try {
        covers.set(url, await renderCover(url));
      } catch (err) {
        console.warn('[Отработано] обложка не нарисовалась:', url, err);
      }
    }),
  );
  return covers;
}
